import React, { useState, useEffect } from 'react';
import { teamsService } from '../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Group as GroupIcon,
  Security as SecurityIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`team-settings-tabpanel-${index}`}
    aria-labelledby={`team-settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const TeamSettings = ({ open, onClose, team, onUpdate }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Email settings removed - configured globally in main settings page

  const [supportTeams, setSupportTeams] = useState([]);
  const [permissions, setPermissions] = useState({
    astreintes: { view: true, add: false, edit: false, delete: false },
    absences: { view: true, add: false, edit: false, delete: false },
    technicians: { view: true, add: false, edit: false, delete: false },
    competences: { view: true, add: false, edit: false, delete: false }
  });



  useEffect(() => {
    if (open && team) {
      loadTeams();
      loadTeamSettings();
    }
  }, [open, team]);

  const loadTeams = async () => {
    try {
      const response = await teamsService.getAll();
      // Exclure l'équipe actuelle de la liste des équipes de support possibles
      setTeams(response.filter(t => t.id !== team?.id));
    } catch (err) {
      setError('Erreur lors du chargement des équipes: ' + err.message);
    }
  };

  const loadTeamSettings = () => {
    if (!team) return;

    // Email settings removed - configured globally in main settings page

    // Charger les équipes de support
    setSupportTeams(team.settings?.supportTeams || []);

    // Charger les permissions (nouvelle structure matricielle)
    const perms = team.settings?.permissions || {};
    setPermissions({
      astreintes: perms.astreintes || { view: true, add: false, edit: false, delete: false },
      absences: perms.absences || { view: true, add: false, edit: false, delete: false },
      technicians: perms.technicians || { view: true, add: false, edit: false, delete: false },
      competences: perms.competences || { view: true, add: false, edit: false, delete: false }
    });
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError('');

    try {
      const updatedSettings = {
        supportTeams,
        permissions
      };

      await teamsService.updateSettings(team.id, updatedSettings);

      setSuccess('Paramètres sauvegardés avec succès');
      
      // Mettre à jour l'équipe locale
      const updatedTeam = {
        ...team,
        settings: {
          ...team.settings,
          ...updatedSettings
        }
      };

      if (onUpdate) {
        onUpdate(updatedTeam);
      }
    } catch (err) {
      setError('Erreur lors de la sauvegarde: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupportTeam = (teamId) => {
    if (!supportTeams.includes(teamId)) {
      setSupportTeams([...supportTeams, teamId]);
    }
  };

  const handleRemoveSupportTeam = (teamId) => {
    setSupportTeams(supportTeams.filter(id => id !== teamId));
  };

  const getAvailableSupportTeams = () => {
    return teams.filter(t => !supportTeams.includes(t.id));
  };

  const getSupportTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : `Équipe ${teamId}`;
  };

  // handleSendTestEmail removed - email testing done in main settings page

  const handlePermissionChange = (category, permission, value) => {
    setPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permission]: value
      }
    }));
  };

  if (!team) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon color="primary" />
          Paramètres de l'équipe - {team.name}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab 
            icon={<GroupIcon />} 
            label="Équipes de Support" 
            id="team-settings-tab-0"
          />
          <Tab 
            icon={<SecurityIcon />} 
            label="Permissions" 
            id="team-settings-tab-1"
          />
        </Tabs>

        {/* Onglet Équipes de Support */}
        <TabPanel value={currentTab} index={0}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Les équipes de support permettent d'afficher les techniciens d'astreinte en renfort dans le mail d'astreinte automatique.</strong>
              <br />
              Ces équipes ne participent pas à la planification de votre équipe, elles apparaissent uniquement à titre informatif pour l'entraide.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom>
            Équipes de Support Configurées
          </Typography>
          
          {supportTeams.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
              Aucune équipe de support configurée
            </Typography>
          ) : (
            <List>
              {supportTeams.map((teamId) => (
                <ListItem key={teamId} divider>
                  <ListItemText
                    primary={getSupportTeamName(teamId)}
                    secondary="Équipe de support pour entraide"
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleRemoveSupportTeam(teamId)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Ajouter une Équipe de Support
          </Typography>
          
          {getAvailableSupportTeams().length === 0 ? (
            <Alert severity="warning">
              Aucune équipe disponible pour ajout en support
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {getAvailableSupportTeams().map((supportTeam) => (
                <Chip
                  key={supportTeam.id}
                  label={supportTeam.name}
                  onClick={() => handleAddSupportTeam(supportTeam.id)}
                  icon={<AddIcon />}
                  color="primary"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          )}
        </TabPanel>

        {/* Onglet Permissions */}
        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Permissions de Délégation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <strong>Ces permissions définissent ce qu'un administrateur d'une AUTRE équipe peut faire ici via délégation.</strong>
            <br />
            Les administrateurs (principal et adjoint) de CETTE équipe ont automatiquement tous les droits.
          </Typography>
          
          <Box sx={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                    Module
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                    Visualisation
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                    Ajout
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                    Modification
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                    Suppression
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries({
                  astreintes: 'Astreintes',
                  absences: 'Absences', 
                  technicians: 'Techniciens',
                  competences: 'Compétences'
                }).map(([key, label]) => (
                  <tr key={key}>
                    <td style={{ padding: '12px', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                      {label}
                    </td>
                    {['view', 'add', 'edit', 'delete'].map((permission) => (
                      <td key={permission} style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={permissions[key]?.[permission] || false}
                              onChange={(e) => handlePermissionChange(key, permission, e.target.checked)}
                              size="small"
                            />
                          }
                          label=""
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Important :</strong> Ces permissions ne concernent QUE les délégations.
              <br />
              • <strong>Administrateurs de cette équipe</strong> : Accès complet automatique
              <br />
              • <strong>Administrateurs délégués</strong> : Limités par ces permissions
            </Typography>
          </Alert>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button 
          onClick={handleSaveSettings} 
          variant="contained" 
          disabled={loading}
        >
          Sauvegarder
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamSettings;